import { z } from 'zod';

/**
 * Skema baris import soal (engine tryout).
 * CSV diparse di BROWSER (papaparse) → dikirim sebagai {rows: [...]} ke
 * POST /api/admin/tryout/banks/:id/import — zod memvalidasi per baris,
 * error dilaporkan per baris (bukan gagal total).
 */

export const EXAM_IMPORT_OPTION_LABELS = ['A', 'B', 'C', 'D', 'E'] as const;

// Header template CSV: public/templates/template-soal.csv
export const EXAM_IMPORT_HEADERS = [
  'question_type', 'body', 'explanation', 'topic', 'difficulty', 'points',
  'option_a', 'option_b', 'option_c', 'option_d', 'option_e', 'correct',
] as const;

const emptyToUndefined = (v: unknown) => (typeof v === 'string' && v.trim() === '' ? undefined : v);

export const examImportRowSchema = z
  .object({
    question_type: z.preprocess(
      emptyToUndefined,
      z.enum(['single_choice', 'multi_choice', 'true_false', 'short_answer']).default('single_choice')
    ),
    body: z.string().min(1, 'body (teks soal) wajib diisi').max(10_000),
    explanation: z.preprocess(emptyToUndefined, z.string().max(10_000).optional()),
    topic: z.preprocess(emptyToUndefined, z.string().max(100).optional()),
    difficulty: z.preprocess(emptyToUndefined, z.coerce.number().int().min(1).max(5).optional()),
    points: z.preprocess(emptyToUndefined, z.coerce.number().int().min(1).max(1000).default(1)),
    option_a: z.preprocess(emptyToUndefined, z.string().max(2000).optional()),
    option_b: z.preprocess(emptyToUndefined, z.string().max(2000).optional()),
    option_c: z.preprocess(emptyToUndefined, z.string().max(2000).optional()),
    option_d: z.preprocess(emptyToUndefined, z.string().max(2000).optional()),
    option_e: z.preprocess(emptyToUndefined, z.string().max(2000).optional()),
    // 'A' | 'A,C' (multi) | teks jawaban (short_answer)
    correct: z.string().min(1, 'correct (kunci jawaban) wajib diisi').max(2000),
  })
  .superRefine((row, ctx) => {
    if (row.question_type === 'short_answer') return; // correct = teks jawaban bebas

    const provided = EXAM_IMPORT_OPTION_LABELS.filter(
      (label) => row[`option_${label.toLowerCase()}` as 'option_a'] !== undefined
    );
    if (provided.length < 2) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Soal pilihan butuh minimal 2 opsi (option_a, option_b, ...)' });
      return;
    }

    const labels = row.correct.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean);
    if (labels.length === 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'correct wajib berisi label opsi (mis. A atau A,C)' });
      return;
    }
    const invalid = labels.filter((l) => !(provided as readonly string[]).includes(l));
    if (invalid.length > 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Label kunci tidak punya opsi: ${invalid.join(', ')}` });
      return;
    }
    if (row.question_type !== 'multi_choice' && labels.length !== 1) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'single_choice/true_false harus tepat 1 kunci' });
    }
  });

export type ExamImportRow = z.infer<typeof examImportRowSchema>;

export interface ExamImportError {
  row: number; // 1-based sesuai urutan rows dikirim
  message: string;
}

export interface ExamImportReport {
  inserted: number;
  errors: ExamImportError[];
}
